<template>
    <section class="relative py-24 bg-white dark:bg-gray-950 overflow-hidden">
        <!-- Blue Half-Circle Backdrop (brochure style) -->
        <div
            class="absolute top-0 left-1/2 -translate-x-1/2 w-[150%] aspect-square bg-primary  -translate-y-[55%] md:-translate-y-[75%] z-0">
        </div>

        <div class="container mx-auto px-4 relative z-10">
            <!-- Title matching brochure header style -->
            <div class="text-center mb-24" :class="isRtl ? 'rtl' : 'ltr'" data-aos="fade-down">
                <h2 class="font-black text-white mb-4 drop-shadow-lg"
                    :class="[isRtl ? 'text-5xl md:text-7xl' : 'text-4xl md:text-6xl']">
                    {{ solutionMainTitle }}
                    <span class="bl ock text-secondary mt-2">{{ solutionSubTitle }}</span>
                </h2>
                <div class="w-32 h-2.5 bg-secondary mx-auto rounded-full mt-8 shadow-lg"></div>
            </div>

            <div class="max-w-5xl mx-auto mb-20">
                <div class="bg-white p-10 md:p-16 rounded-[4rem] shadow-2xl border border-gray-100 relative group"
                    data-aos="fade-up">
                    <div
                        class="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-secondary rounded-full flex items-center justify-center text-primary font-black text-4xl shadow-xl z-20 transition-transform group-hover:scale-110">
                        <!-- {{ isRtl ? '٧' : '7' }} -->
                    </div>

                    <p class="text-gray-700 leading-[2.2] font-black text-center mb-6"
                        :class="[isRtl ? 'text-2xl md:text-3xl rtl' : 'text-xl md:text-2xl ltr']">
                        {{ solutionDesc }}
                    </p>
                    <p class="text-gray-600 leading-[2.2] font-medium text-center"
                        :class="[isRtl ? 'text-xl md:text-2xl rtl' : 'text-lg md:text-xl ltr']">
                        {{ solutionDescSecondary }}
                    </p>
                </div>
            </div>

            <!-- Detailed categories from the text -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" :class="isRtl ? 'rtl' : 'ltr'">
                <Link v-for="(cat, index) in industryCategories" :key="index" :href="route('products')" data-aos="zoom-in"
                    :data-aos-delay="index * 100"
                    class="bg-slate-50 p-6 rounded-3xl border border-gray-100 text-center hover:bg-primary group transition-all duration-500 cursor-pointer">
                    <div
                        class="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm group-hover:rotate-12 transition-transform">
                        <component :is="cat.icon" class="w-8 h-8 text-primary" />
                    </div>
                    <h4 class="text-lg font-black text-primary group-hover:text-white transition-colors">{{ cat.title }}
                    </h4>
                </Link>
            </div>


        </div>
        <!-- Quality Testing Section (matches the new image) -->
        <div class="mt-32 relative group overflow-hidden  bg-primary shadow-2xl   px-4 px-md-16" data-aos="fade-up">
            <!-- Background Decor -->
            <div
                class="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:bg-white/10 transition-colors">
            </div>
            <div
                class="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl group-hover:bg-secondary/20 transition-colors">
            </div>

            <div class="relative z-10 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 p-8 md:p-16">
                <!-- Image/Illustration Side -->
                <div class="relative overflow-hidden rounded-2xl  border border-white/10 aspect-video lg:aspect-square flex items-center justify-center "
                    data-aos="fade-right">
                    <img src="/assets/almatin/11 (10).png" class="w-full h-full object-contain" alt="Testing Machine" />
                    <!-- Overlay status (like in the image) -->
                    <div
                        class="absolute top-4 right-4 bg-green-500/90 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest backdrop-blur-sm shadow-lg flex items-center gap-2">
                        <div class="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        Validated
                    </div>
                </div>

                <!-- Text Content Side -->
                <div class="text-right flex flex-col items-end" :class="isRtl ? 'rtl' : 'ltr'" data-aos="fade-left">
                    <div class="w-20 h-1.5 bg-secondary rounded-full mb-8"></div>
                    <h3 class="text-3xl md:text-5xl font-black text-white mb-8 leading-tight drop-shadow-md">
                        {{ testingTitle }}
                    </h3>
                    <p class="text-blue-50 leading-[2.2] text-xl md:text-2xl font-black opacity-90 max-w-2xl">
                        {{ testingDesc }}
                    </p>

                    <div class="grid grid-cols-2 gap-4 mt-12 w-full">
                        <div v-for="tagKey in testingTags" :key="tagKey"
                            class="bg-white/10 backdrop-blur-md px-4 py-3 rounded-xl border border-white/5 text-white font-bold text-sm text-center hover:bg-white/20 transition-colors">
                            {{ __t(tagKey) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import {
    BeakerIcon,
    TruckIcon,
    BuildingOffice2Icon,
    BoltIcon,
    CubeIcon,
    SunIcon,
    Square3Stack3DIcon
} from '@heroicons/vue/24/outline'
import { Link, usePage } from '@inertiajs/vue3'
import { inject, computed } from 'vue'

const __t = inject('__t')
const page = usePage()
const isRtl = computed(() => page.props.locale === 'ar')

const solutionMainTitle = __t('home_solutions_title')
const solutionSubTitle = __t('home_solutions_subtitle')
const solutionDesc = __t('home_solutions_description')
const solutionDescSecondary = __t('home_solutions_description_secondary')
const testingTitle = __t('home_testing_title')
const testingDesc = __t('home_testing_desc')

const industryCategories = [
    { title: __t('home_cat_packaging'), icon: CubeIcon },
    { title: __t('home_cat_agriculture'), icon: SunIcon },
    { title: __t('home_cat_construction'), icon: Square3Stack3DIcon },
    { title: __t('home_cat_infrastructure'), icon: BuildingOffice2Icon },
    { title: __t('home_cat_plastic'), icon: BeakerIcon }
]

const testingTags = [
    'home_test_pressure',
    'home_test_bending',
    'home_test_density',
    'home_test_flowability'
]

const indexPoints = [
    'مسيرة المتين عبر العقود',
    'الطفرة الصناعية الكبرى',
    'من حمص إلى العالم',
    'رؤيتنا المستقبلية',
    'قيمنا وثقافة النجاح',
    'المسؤولية الاجتماعية',
    'شركاؤنا الاستراتيجيون',
    'نحو أسواق العالم',
    'شهادات وجوائز',
    'خطواتنا القادمة',
    'تواصل معنا'
]
</script>
